// src/components/SimpleImageUpload.tsx
import { useState, useRef } from 'react';
import { Upload, X, Loader, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { compressImage } from '../utils/imageOptimizer';

interface SimpleImageUploadProps {
  onImageUploaded?: (url: string) => void;
  currentImage?: string;
  onRemove?: () => void;
  className?: string;
}

export function SimpleImageUpload({ onImageUploaded, currentImage, onRemove, className = '' }: SimpleImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize with current image
  if (currentImage && !imageUrl) {
    setImageUrl(currentImage);
  }

  const uploadImage = async (file: File) => {
    try {
      setUploading(true);
      setError(null);
      
      // 1. COMPRESS THE IMAGE
      const optimizedFile = await compressImage(file);

      // 2. Generate path (Always .webp now)
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.webp`;
      const filePath = `${fileName}`;

      // 3. Upload to Supabase with Aggressive Caching
      const { error: uploadError } = await supabase.storage
        .from('listings') // Make sure this bucket matches your setup ('listings' or 'images')
        .upload(filePath, optimizedFile, {
          cacheControl: '31536000', // 1 Year Cache
          upsert: false,
          contentType: 'image/webp' // Explicit content type
        });

      if (uploadError) throw uploadError;

      // 4. Get Public URL
      const { data: { publicUrl } } = supabase.storage
        .from('listings') // Match bucket name above
        .getPublicUrl(filePath);

      setImageUrl(publicUrl);
      
      if (typeof onImageUploaded === 'function') {
        onImageUploaded(publicUrl);
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err instanceof Error ? err.message : 'Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Increased raw limit because we compress it anyway, 
    // but good to keep a sanity check (e.g. 20MB)
    if (file.size > 20 * 1024 * 1024) {
      setError('Image is too large (Max 20MB)');
      return;
    }

    await uploadImage(file);
    
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = () => {
    setImageUrl(null);
    setError(null);
    if (onRemove) onRemove();
  };

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Create a single div with a background image
  const containerStyle = {
    backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };

  return (
    <div className={className}>
      <div 
        className="relative w-full h-64 border-2 border-gray-200 rounded-lg overflow-hidden cursor-pointer group"
        style={containerStyle}
        onClick={handleClick}
      >
        {/* Always render the input element */}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/*"
          onChange={handleFileChange}
          disabled={uploading}
        />
        
        {/* Overlay with controls */}
        <div 
          className={`absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center ${
            imageUrl ? 'opacity-0 group-hover:opacity-100' : 'opacity-0'
          }`}
        >
          {imageUrl && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              className="bg-red-600 text-white p-3 rounded-full hover:bg-red-700 transform scale-90 group-hover:scale-100 transition-all"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        
        {/* Upload area - conditionally visible */}
        <div 
          className={`absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-200 ${
            !imageUrl ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          {uploading ? (
            <>
              <Loader className="h-12 w-12 text-[#D4452C] animate-spin mb-4" />
              <p className="text-sm text-gray-600 font-medium">Optimisation & Upload...</p>
            </>
          ) : (
            <>
              <Upload className="h-12 w-12 text-gray-400 mb-4 group-hover:text-[#D4452C] transition-colors" />
              <p className="mb-2 text-sm text-gray-600">
                <span className="font-semibold">Click to upload</span> or drag and drop
              </p>
              <p className="text-xs text-gray-500">Auto-compressed to WebP</p>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
          <ImageIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
    </div>
  );
}